import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const fromData = parseInt(this.dataset.cartCount ?? '', 10);
    if (Number.isFinite(fromData)) return fromData;

    const n = parseInt(this.refs.cartBubbleCount?.textContent ?? '0', 10);
    return Number.isFinite(n) ? n : 0;
  }

  set currentCartCount(value) {
    const count = Math.max(0, Number(value) || 0);
    this.dataset.cartCount = String(count);
    this.refs.cartBubbleCount.textContent = count < 100 ? String(count) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    // Seed dataset from Liquid-rendered count once
    if (this.dataset.cartCount == null) {
      const n = parseInt(this.refs.cartBubbleCount?.textContent ?? '0', 10);
      this.dataset.cartCount = String(Number.isFinite(n) ? n : 0);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Prefer absolute cart.item_count. Only add when explicitly optimistic.
    // Ignore events without itemCount — they would incorrectly reset the badge to 0.
    const data = event.detail?.data || {};
    if (data.itemCount == null && data.optimistic !== true) return;

    const itemCount = Number(data.itemCount ?? 0);
    const optimistic = data.optimistic === true;

    this.renderCartBubble(itemCount, optimistic);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Cart total, or delta when optimistic.
   * @param {boolean} optimistic - When true, add itemCount to current badge.
   */
  renderCartBubble = async (itemCount, optimistic = false, animate = true) => {
    const nextCount = optimistic
      ? Math.max(0, (Number(this.currentCartCount) || 0) + Number(itemCount || 0))
      : Math.max(0, Number(itemCount) || 0);

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', nextCount > 0 && animate);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(nextCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   * Only apply sessionStorage when it matches a recent optimistic update —
   * never overwrite a server-rendered empty cart with a stale count.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    const visibleCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const count = parseInt(value, 10);

      if (Number.isNaN(count) || count < 0) {
        sessionStorage.removeItem('cart-count');
        return;
      }

      // Server already matches session — nothing to do
      if (count === visibleCount) return;

      // Stale session after clear/reload: trust the server-rendered count
      if (Date.now() - timestamp >= 10000 || visibleCount === 0) {
        this.dataset.cartCount = String(visibleCount);
        sessionStorage.setItem(
          'cart-count',
          JSON.stringify({
            value: String(visibleCount),
            timestamp: Date.now(),
          })
        );
        return;
      }

      this.renderCartBubble(count, false, false);
    } catch (_) {
      sessionStorage.removeItem('cart-count');
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
